import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, CalendarDays, ChefHat, IdCard, Plus, Printer, Sparkles, Trash2, UserPlus, Wallet } from 'lucide-react';
import {
  addDays,
  formatDateSpan,
  formatMoney,
  shopDate,
  type Employee,
  type PayItem,
  type Payslip,
  type PayslipWithDetail,
  type ShiftDayType,
  type WorkShift,
} from '@bakery/shared';

import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { PositionsModal } from './Positions.js';
import { printPayslip } from '../lib/printing.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  MoneyInput,
  SectionHeading,
  Select,
  Skeleton,
  Tabs,
  Textarea,
  cx,
  useToast,
} from '../components/ui.js';

import { tidyDraft } from '../lib/number-field.js';

type EmployeeWithPay = Employee & { payItems: PayItem[] };

export function StaffScreen() {
  const { can } = useAuth();
  const [tab, setTab] = useState<'team' | 'roster' | 'payroll'>('team');
  const [addOpen, setAddOpen] = useState(false);

  const [positionsOpen, setPositionsOpen] = useState(false);

  /*
   * Former staff are fetched with everyone else and split below. Their records
   * are kept — payslips name them, and "who was on that week" outlives the
   * person — so the Team tab has a view for them rather than pretending they
   * were never here.
   */
  const employees = useQuery({ queryKey: ['employees', 'all'], queryFn: () => api.staff.employees(true) });
  const active = (employees.data ?? []).filter((employee) => employee.isActive);

  // The payslip builder issues pay for anyone, so it is payroll-only. Someone
  // with just "payroll.own" sees their own slips on their own record instead.
  const canRunPayroll = can('payroll.all');
  const canEditTeam = can('staff.edit');

  return (
    <div className="mx-auto max-w-5xl px-4 py-5">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Team &amp; payroll</h1>
          <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">
            Staff, their rates, the roster, and printable payslips.
          </p>
        </div>
        {canEditTeam && (
          <div className="flex flex-wrap gap-2">
            <Button icon={<IdCard className="size-4" />} onClick={() => setPositionsOpen(true)}>
              Positions
            </Button>
            <Button variant="primary" icon={<UserPlus className="size-4" />} onClick={() => setAddOpen(true)}>
              Add employee
            </Button>
          </div>
        )}
      </header>

      <div className="mb-4">
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'team' as const, label: 'Team', count: active.length },
            { value: 'roster' as const, label: 'Roster' },
            ...(canRunPayroll ? [{ value: 'payroll' as const, label: 'Payslips' }] : []),
          ]}
        />
      </div>

      {tab === 'team' && <TeamTab employees={employees.data ?? []} loading={employees.isLoading} />}
      {/* The rota and the payslip builder are about people who are still here. */}
      {tab === 'roster' && <RosterTab employees={active} />}
      {tab === 'payroll' && canRunPayroll && <PayrollTab employees={active} />}

      {addOpen && <EmployeeModal onClose={() => setAddOpen(false)} />}
      {positionsOpen && (
        <PositionsModal onClose={() => setPositionsOpen(false)} />
      )}
    </div>
  );
}

/**
 * The three statutory contributions every Philippine payslip carries.
 *
 * Labels only. Each agency sets its own contribution table and revises it by
 * circular, so a figure computed here would be quietly wrong the month after
 * one changed — and a wrong deduction is the employee's money.
 */
const STATUTORY_DEDUCTIONS = ['SSS', 'PhilHealth', 'Pag-IBIG'] as const;

const PAY_TYPE_LABEL: Record<string, string> = {
  monthly: 'Monthly salary',
  daily: 'Daily rate',
  hourly: 'Hourly',
  per_shift: 'Per shift',
  piece_rate: 'Per piece',
};

/**
 * What somebody is paid, in one phrase.
 *
 * Only the rate their pay type actually uses. An employee row carries all four
 * rate columns whatever their pay type, so reading the wrong one shows a
 * confident ₱0 — and the monthly figure was the only one ever shown, which
 * left everybody on a daily rate with no pay on their card at all.
 */
function payLine(employee: Employee): string | null {
  const rate = {
    monthly: employee.monthlySalary,
    daily: employee.dailyRate,
    hourly: employee.hourlyRate,
    per_shift: employee.shiftRate,
    piece_rate: null,
  }[employee.payType];

  if (rate === null || rate === undefined) return null;
  const per = { monthly: 'month', daily: 'day', hourly: 'hour', per_shift: 'shift' }[
    employee.payType as 'monthly' | 'daily' | 'hourly' | 'per_shift'
  ];
  return `${formatMoney(rate)} / ${per}`;
}

function TeamTab({ employees, loading }: { employees: EmployeeWithPay[]; loading: boolean }) {
  const [editing, setEditing] = useState<EmployeeWithPay | null>(null);
  const [showing, setShowing] = useState<'here' | 'former'>('here');

  const here = employees.filter((employee) => employee.isActive);
  const former = employees.filter((employee) => !employee.isActive);

  /*
   * The toggle only appears once somebody has left, so putting the last of
   * them back would otherwise strand you on an empty "Nobody has left" pane
   * with nothing on screen to click your way out of.
   */
  const viewing = former.length === 0 ? 'here' : showing;
  const shown = viewing === 'here' ? here : former;

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-20" />
        ))}
      </div>
    );
  }

  if (employees.length === 0) {
    return <EmptyState icon={<ChefHat className="size-8" />} title="No staff yet" description="Add your bakers, cashiers and riders to start tracking shifts and pay." />;
  }

  return (
    <>
      {/*
        * Only offered once somebody has actually left. A tab that is always
        * empty is a tab that teaches people to ignore it.
        */}
      {former.length > 0 && (
        <div className="mb-3 flex gap-2">
          {(
            [
              ['here', `On the team (${here.length})`],
              ['former', `Former (${former.length})`],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setShowing(value)}
              className={cx(
                'rounded-[var(--radius-md)] px-3 py-1.5 text-sm font-medium transition',
                viewing === value
                  ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]'
                  : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-sunken)]',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <EmptyState
          icon={<ChefHat className="size-8" />}
          title={viewing === 'former' ? 'Nobody has left' : 'Nobody on the team'}
        />
      ) : (
        <ul className="space-y-2">
          {shown.map((employee) => (
            <li key={employee.id}>
              <button
                onClick={() => setEditing(employee)}
                className={cx(
                  'card flex w-full items-center gap-4 p-4 text-left transition hover:border-crust-400',
                  !employee.isActive && 'opacity-70',
                )}
              >
                <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[var(--color-accent-soft)] font-display text-sm font-semibold text-[var(--color-accent-ink)]">
                  {employee.firstName.slice(0, 1)}
                  {employee.lastName.slice(0, 1)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{employee.displayName}</p>
                  <p className="truncate text-xs text-[var(--color-ink-soft)]">
                    {employee.employeeNo} · {PAY_TYPE_LABEL[employee.payType]}
                    {payLine(employee) && ` · ${payLine(employee)}`}
                  </p>
                  {!employee.isActive && (
                    <p className="truncate text-xs text-[var(--color-ink-faint)]">
                      Left {employee.endedOn ?? 'the shop'}
                      {employee.leavingReason && ` — ${employee.leavingReason}`}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap justify-end gap-1">
                  {/*
                    * Piece rates belong to people paid by the piece. Showing
                    * them for anyone else is how a baker moved onto a daily
                    * wage still reads as being paid per loaf.
                    */}
                  {employee.payType === 'piece_rate' &&
                    employee.payItems
                      .filter((item) => item.isActive)
                      .map((item) => (
                        <Badge key={item.id} tone="brand">
                          {item.label} {formatMoney(item.rate, { compactWhole: true })}
                        </Badge>
                      ))}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {editing && <EmployeeModal employee={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function RosterTab({ employees }: { employees: EmployeeWithPay[] }) {
  const queryClient = useQueryClient();
  const [weekStart, setWeekStart] = useState(() => shopDate());
  const [adding, setAdding] = useState<{ employeeId: string; date: string } | null>(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)), [weekStart]);

  const shifts = useQuery({
    queryKey: ['work-shifts', weekStart],
    queryFn: () =>
      api.staff.shifts({ from: `${weekStart}T00:00:00.000Z`, to: `${addDays(weekStart, 7)}T00:00:00.000Z` }),
  });

  const [editing, setEditing] = useState<WorkShift | null>(null);

  return (
    <Card className="overflow-x-auto p-4">
      <SectionHeading
        title="Weekly roster"
        hint="Tap an empty cell to schedule someone, or a shift to change it. Every shift rostered here counts as a day worked on the payslip unless you mark it absent."
        action={
          <div className="flex flex-col items-end gap-1.5">
            {/* Which week this is — the thing paging used to leave unanswered. */}
            <span className="text-sm font-medium whitespace-nowrap text-[var(--color-ink)]">
              {formatDateSpan(days[0]!, days[6]!)}
            </span>
            <div className="flex gap-1">
              <Button size="sm" onClick={() => setWeekStart(addDays(weekStart, -7))}>Previous</Button>
              <Button size="sm" onClick={() => setWeekStart(shopDate())}>This week</Button>
              <Button size="sm" onClick={() => setWeekStart(addDays(weekStart, 7))}>Next</Button>
            </div>
          </div>
        }
      />

      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="text-left text-xs text-[var(--color-ink-soft)]">
            <th className="w-44 pb-2 font-medium">Employee</th>
            {days.map((day, index) => {
              const date = new Date(`${day}T00:00:00Z`);
              /*
               * The month is named on the first column and again wherever the
               * week crosses into a new one, so a roster running from the 29th
               * to the 5th says which 5th it means. Repeating it on all seven
               * would only be noise.
               */
              const opensMonth =
                index === 0 || new Date(`${days[index - 1]}T00:00:00Z`).getUTCMonth() !== date.getUTCMonth();

              /*
               * Assembled by hand: asking the locale for { weekday, day } in
               * Philippine English gives "5 Mon", and hanging a month off that
               * reads "5 Mon Oct".
               */
              const weekday = date.toLocaleDateString('en-PH', { weekday: 'short', timeZone: 'UTC' });
              const monthShort = date.toLocaleDateString('en-PH', { month: 'short', timeZone: 'UTC' });

              return (
                <th key={day} className="pb-2 text-center font-medium">
                  {weekday} {date.getUTCDate()}
                  {opensMonth && <span className="ml-1 text-[var(--color-ink-faint)]">{monthShort}</span>}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-line)]">
          {employees.map((employee) => (
            <tr key={employee.id}>
              <td className="py-2 pr-2 text-sm font-medium">{employee.displayName}</td>
              {days.map((day) => {
                const dayShifts = (shifts.data ?? []).filter(
                  (shift) => shift.employeeId === employee.id && shift.startsAt.slice(0, 10) === day,
                );
                return (
                  <td key={day} className="px-1 py-1.5 align-top">
                    {dayShifts.length === 0 ? (
                      <button
                        onClick={() => setAdding({ employeeId: employee.id, date: day })}
                        className="h-11 w-full rounded-[var(--radius-sm)] border border-dashed border-[var(--color-line-strong)] text-[var(--color-ink-faint)] transition hover:border-crust-400 hover:text-crust-600"
                        aria-label={`Schedule ${employee.displayName} on ${day}`}
                      >
                        <Plus className="mx-auto size-3.5" />
                      </button>
                    ) : (
                      dayShifts.map((shift) => (
                        <button
                          key={shift.id}
                          onClick={() => setEditing(shift)}
                          title="Click to change this shift"
                          className={cx(
                            'mb-1 block w-full rounded-[var(--radius-sm)] px-2 py-1.5 text-[11px] leading-tight transition hover:brightness-95',
                            shift.status === 'absent'
                              ? 'bg-[var(--color-danger-soft)] text-[var(--color-danger)] line-through'
                              : shift.dayType !== 'regular'
                                ? 'bg-[var(--color-warn-soft)] text-[var(--color-warn)]'
                                : 'bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]',
                          )}
                        >
                          {shift.label ?? 'Shift'}
                          <span className="block opacity-70">
                            {shift.startsAt.slice(11, 16)}–{shift.endsAt.slice(11, 16)}
                          </span>
                          {/* Only the exceptions are worth the width. */}
                          {shift.status === 'absent' && <span className="block font-medium">Absent</span>}
                          {shift.dayType !== 'regular' && (
                            <span className="block font-medium">
                              {shift.dayType === 'regular_holiday' ? 'Holiday' : 'Special day'}
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {adding && (
        <ShiftModal
          employee={employees.find((e) => e.id === adding.employeeId)!}
          date={adding.date}
          onClose={() => setAdding(null)}
        />
      )}
      {editing && <ShiftModal shift={editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

/**
 * What a shift opens on for somebody with no hours recorded yet.
 *
 * A last resort, not a policy: everyone who works here should have their own
 * hours on their card. It exists so a brand-new employee's first shift starts
 * from a working day rather than an empty pair of time fields.
 */
const FALLBACK_SHIFT = { start: '09:00', end: '18:00' };

const DAY_TYPES: { value: ShiftDayType; label: string }[] = [
  { value: 'regular', label: 'Ordinary day' },
  { value: 'regular_holiday', label: 'Regular holiday (pays double)' },
  { value: 'special_non_working', label: 'Special non-working day (pays 130%)' },
];

/**
 * Scheduling a shift, and afterwards saying what became of it.
 *
 * One dialog for both, because a shift is one thing whose details are filled
 * in over time: the hours when the rota is drawn up, whether it was a holiday
 * as soon as that is known, and an absence only if it happens.
 */
function ShiftModal({
  employee,
  date,
  shift,
  onClose,
}: {
  employee?: EmployeeWithPay;
  date?: string;
  shift?: WorkShift;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const day = shift ? shift.startsAt.slice(0, 10) : date!;

  /*
   * Prefilled from the hours this person actually works. It used to open on
   * 05:00–13:00 "Morning bake" for everybody, which is nobody's shift here —
   * so every cell on the rota had to be corrected before it was right.
   *
   * `||` rather than `??`, deliberately. An employee saved without their hours
   * filled in holds an empty string, not null, and `??` would have handed that
   * straight to the time input — opening the rota on a blank time, which is
   * worse than opening it on the wrong one.
   */
  const [label, setLabel] = useState(shift?.label ?? 'Shift');
  const [start, setStart] = useState(
    shift?.startsAt.slice(11, 16) || employee?.defaultShiftStart || FALLBACK_SHIFT.start,
  );
  const [end, setEnd] = useState(
    shift?.endsAt.slice(11, 16) || employee?.defaultShiftEnd || FALLBACK_SHIFT.end,
  );
  const [dayType, setDayType] = useState<ShiftDayType>(shift?.dayType ?? 'regular');
  const [absent, setAbsent] = useState(shift?.status === 'absent');
  const [absenceReason, setAbsenceReason] = useState(shift?.absenceReason ?? '');

  const done = () => {
    queryClient.invalidateQueries({ queryKey: ['work-shifts'] });
    // The payslip is built from these, so it has to be re-read alongside.
    queryClient.invalidateQueries({ queryKey: ['payslip-draft'] });
    onClose();
  };

  const body = {
    // Manila is UTC+8 year round, so the offset can be written directly.
    startsAt: `${day}T${start}:00.000+08:00`,
    endsAt: `${day}T${end}:00.000+08:00`,
    label,
    dayType,
    status: absent ? 'absent' : shift && shift.status !== 'absent' ? shift.status : 'scheduled',
    absenceReason: absent ? absenceReason.trim() || null : null,
  };

  const save = useMutation({
    mutationFn: () =>
      shift
        ? api.staff.updateShift(shift.id, body)
        : api.staff.createShift({ employeeId: employee!.id, ...body }),
    onSuccess: done,
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const remove = useMutation({
    mutationFn: () => api.staff.deleteShift(shift!.id),
    onSuccess: done,
  });

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={shift ? 'This shift' : 'Schedule a shift'}
      description={shift ? undefined : employee?.displayName}
      footer={
        <>
          {shift && (
            <Button
              variant="ghost"
              className="text-[var(--color-danger)]"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Remove
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            {shift ? 'Save' : 'Schedule'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="What for">
          <Input value={label} onChange={(event) => setLabel(event.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Starts">
            <Input type="time" value={start} onChange={(event) => setStart(event.target.value)} />
          </Field>
          <Field label="Ends">
            <Input type="time" value={end} onChange={(event) => setEnd(event.target.value)} />
          </Field>
        </div>

        <Field label="Kind of day" hint="Holiday premiums are worked out from this.">
          <Select value={dayType} onChange={(event) => setDayType(event.target.value as ShiftDayType)}>
            {DAY_TYPES.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Select>
        </Field>

        {/*
          * The only attendance the shop has to key in. A rostered shift counts
          * as worked, so this is the exception — which is how the paper slip
          * reads too: days worked, then shifts absent, then the reason.
          */}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={absent}
            onChange={(event) => setAbsent(event.target.checked)}
            className="size-4 accent-[var(--color-danger)]"
          />
          They did not work this shift
        </label>

        {absent && (
          <Field label="Reason" hint="Printed on the payslip beside the absence.">
            <Input
              value={absenceReason}
              onChange={(event) => setAbsenceReason(event.target.value)}
              placeholder="Sick, personal, no schedule…"
            />
          </Field>
        )}
      </div>
    </Modal>
  );
}

/**
 * The payslip builder. It mirrors the bakery's paper slip: a daily grid per pay
 * item, totals, named adjustments, then a grand total — computed, previewed,
 * and only then issued.
 */
function PayrollTab({ employees }: { employees: EmployeeWithPay[] }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const today = shopDate();
  const [employeeId, setEmployeeId] = useState(employees[0]?.id ?? '');
  const [periodStart, setPeriodStart] = useState(() => addDays(today, -14));
  const [periodEnd, setPeriodEnd] = useState(today);
  const [adjustments, setAdjustments] = useState<{ label: string; amount: number; kind: 'addition' | 'deduction' }[]>([]);
  /*
   * The slip this one is being issued in place of, if any.
   *
   * "Edit" on a payslip does not rewrite it where it lies. The figures are
   * derived from the work log and the rates, so the honest way to change one
   * is to build it again and drop the old — which is what this holds on to
   * until the replacement is safely issued.
   */
  const [replacing, setReplacing] = useState<{ id: string; payslipNo: string } | null>(null);

  const employee = employees.find((e) => e.id === employeeId) ?? employees[0];

  const logs = useQuery({
    queryKey: ['work-logs', employeeId, periodStart, periodEnd],
    queryFn: () => api.staff.workLogs(employeeId, periodStart, periodEnd),
    enabled: Boolean(employeeId),
  });

  /*
   * The adjustments are deliberately NOT part of the key.
   *
   * They were, and every keystroke in an adjustment's label therefore made a
   * new query: React Query flipped to `isLoading`, the whole table was
   * replaced by a skeleton, and the input being typed into was unmounted. The
   * effect at the keyboard was losing focus after each letter and having to
   * click back into the field — which is exactly what the shop reported.
   *
   * Nothing about the lines depends on an adjustment anyway. They are named
   * sums added to or taken off the end, so they are applied below, in the
   * browser, where they cost nothing and appear as they are typed.
   */
  const draft = useQuery({
    queryKey: ['payslip-draft', employeeId, periodStart, periodEnd],
    queryFn: () => api.staff.draftPayslip({ employeeId, periodStart, periodEnd }),
    enabled: Boolean(employeeId),
    // One more guard on the same problem: changing the period keeps the old
    // table on screen instead of collapsing it to a skeleton.
    placeholderData: (previous) => previous,
  });

  const roles = useQuery({ queryKey: ['roles'], queryFn: api.staff.roles });
  const roleName = roles.data?.find((role) => role.id === employee?.roleId)?.name ?? null;

  /*
   * The last slip this person was issued, read only to offer the statutory
   * amounts again. They are the same figure period after period until an
   * agency changes its table, so retyping them every fortnight is pure
   * transcription — and transcription is where a wrong deduction comes from.
   */
  const previous = useQuery({
    queryKey: ['payslips', 'previous', employeeId],
    queryFn: async () => {
      const slips = await api.staff.payslips(employeeId);
      return slips.length > 0 ? api.staff.payslip(slips[0]!.id) : null;
    },
    enabled: Boolean(employeeId),
  });

  const lastAmountFor = (label: string) =>
    previous.data?.adjustments.find((a) => a.label === label && a.kind === 'deduction')?.amount ?? 0;

  const subtotal = draft.data?.draft.subtotal ?? 0;
  const additions = adjustments
    .filter((a) => a.kind === 'addition')
    .reduce((sum, a) => sum + a.amount, 0);
  const deductions = adjustments
    .filter((a) => a.kind === 'deduction')
    .reduce((sum, a) => sum + a.amount, 0);
  const grandTotal = subtotal + additions - deductions;

  const saveLog = useMutation({
    mutationFn: (entries: { payItemId: string; workDate: string; qty: number }[]) =>
      api.staff.saveWorkLogs(employeeId, entries),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-logs'] });
      queryClient.invalidateQueries({ queryKey: ['payslip-draft'] });
    },
  });

  const autoFill = useMutation({
    mutationFn: () => api.staff.autoFill(employeeId, periodStart, periodEnd),
    onSuccess: (filled) => {
      queryClient.invalidateQueries({ queryKey: ['work-logs'] });
      queryClient.invalidateQueries({ queryKey: ['payslip-draft'] });
      toast.push({
        tone: filled.length > 0 ? 'ok' : 'neutral',
        title: filled.length > 0 ? `Filled ${filled.length} day${filled.length === 1 ? '' : 's'}` : 'Nothing to fill',
        body: filled.length === 0 ? 'No production runs or deliveries recorded in this period.' : undefined,
      });
    },
  });

  const issue = useMutation({
    mutationFn: async () => {
      const payslip = await api.staff.issuePayslip({ employeeId, periodStart, periodEnd, adjustments });
      /*
       * The old one goes only once the new one exists. If issuing fails, the
       * slip being corrected is still there — the alternative is a shop with
       * no payslip at all for that fortnight.
       */
      if (replacing) {
        try {
          await api.staff.deletePayslip(replacing.id);
        } catch {
          // Paid slips are refused by the server, which is correct. Say so
          // rather than pretending the replacement tidied itself up.
          toast.push({
            tone: 'warn',
            title: `${replacing.payslipNo} is still on file`,
            body: 'It is marked paid, so it was kept. Void it if it should show as cancelled.',
          });
        }
      }
      return payslip;
    },
    onSuccess: (payslip) => {
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
      toast.push({
        tone: 'ok',
        title: replacing ? `${payslip.payslipNo} replaces ${replacing.payslipNo}` : `${payslip.payslipNo} issued`,
        body: formatMoney(payslip.grandTotal),
      });
      setReplacing(null);
    },
  });

  const dates = useMemo(() => {
    const out: string[] = [];
    for (let date = periodStart; date <= periodEnd; date = addDays(date, 1)) out.push(date);
    return out;
  }, [periodStart, periodEnd]);

  if (!employee) {
    return <EmptyState icon={<Wallet className="size-8" />} title="Add an employee first" />;
  }

  /*
   * The daily counts grid is how piece-rate pay is worked out, and nothing
   * else. `computePayslip` has always ignored these counts for everyone
   * else — a daily-rate payslip is days worked times the daily rate — so
   * showing the grid to them was asking for numbers that would never be paid.
   */
  const payItems =
    employee.payType === 'piece_rate' ? employee.payItems.filter((item) => item.isActive) : [];
  const qtyFor = (payItemId: string, date: string) =>
    logs.data?.find((log) => log.payItemId === payItemId && log.workDate === date)?.qty ?? '';

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Employee">
            <Select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)}>
              {employees.map((option) => (
                <option key={option.id} value={option.id}>{option.displayName}</option>
              ))}
            </Select>
          </Field>
          <Field label="Period start">
            <Input type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} />
          </Field>
          <Field label="Period end">
            <Input type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} />
          </Field>
        </div>
      </Card>

      {payItems.length > 0 && (
        <Card className="overflow-x-auto p-4">
          <SectionHeading
            title="Daily counts"
            hint="What this person produced or delivered each day."
            action={
              <Button size="sm" icon={<Sparkles className="size-4" />} loading={autoFill.isPending} onClick={() => autoFill.mutate()}>
                Fill from records
              </Button>
            }
          />

          <table className="w-full min-w-[420px] text-sm">
            <thead>
              <tr className="text-xs text-[var(--color-ink-soft)]">
                <th className="pb-2 text-left font-medium">Date</th>
                {payItems.map((item) => (
                  <th key={item.id} className="pb-2 text-center font-medium">
                    {item.label}
                    <span className="block font-normal opacity-70">{formatMoney(item.rate)} / {item.unitLabel.replace(/s$/, '')}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-line)]">
              {dates.map((date) => (
                <tr key={date}>
                  <td className="py-1 text-xs text-[var(--color-ink-soft)]">
                    {new Date(`${date}T00:00:00Z`).toLocaleDateString('en-PH', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })}
                  </td>
                  {payItems.map((item) => (
                    <td key={item.id} className="px-1 py-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        defaultValue={qtyFor(item.id, date)}
                        /*
                         * Uncontrolled — it saves when you leave the cell, not
                         * on every keystroke — so it never grows the stale
                         * `02` that controlled boxes did. It still needs the
                         * selection, or tabbing across a week of zeroes means
                         * clearing each one by hand before typing.
                         */
                        onFocus={(event) => event.target.select()}
                        onBlur={(event) => {
                          const qty = Number(tidyDraft(event.target.value));
                          if (Number.isFinite(qty)) saveLog.mutate([{ payItemId: item.id, workDate: date, qty }]);
                        }}
                        className="tabular h-9 w-full rounded-[var(--radius-sm)] border border-[var(--color-line)] bg-transparent px-2 text-center text-sm focus:border-crust-500 focus:outline-none"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card className="p-4">
        <SectionHeading title="Payslip" hint={`${periodStart} to ${periodEnd}`} />

        {replacing && (
          <div className="mb-3 flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-info)]/40 bg-[var(--color-info-soft)] px-3 py-2 text-sm">
            <span className="min-w-0 flex-1">
              Correcting <strong>{replacing.payslipNo}</strong>. Issuing this will replace it.
            </span>
            <Button size="sm" variant="ghost" onClick={() => setReplacing(null)}>
              Keep both
            </Button>
          </div>
        )}

        {draft.isLoading ? (
          <Skeleton className="h-32" />
        ) : (
          <>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-[var(--color-line)]">
                {draft.data?.draft.lines.map((line) => (
                  <tr key={line.label}>
                    <td className="py-2">{line.label}</td>
                    <td className="tabular py-2 text-right text-[var(--color-ink-soft)]">
                      {line.qty} {line.unitLabel} × {formatMoney(line.rate)}
                    </td>
                    <td className="tabular py-2 pl-4 text-right font-medium">{formatMoney(line.amount)}</td>
                  </tr>
                ))}

                {adjustments.map((adjustment, index) => (
                  <tr key={index}>
                    <td className="py-2" colSpan={2}>
                      <div className="flex items-center gap-2">
                        <Input
                          value={adjustment.label}
                          onChange={(event) =>
                            setAdjustments((current) => current.map((a, i) => (i === index ? { ...a, label: event.target.value } : a)))
                          }
                          className="h-9 max-w-56"
                        />
                        <Select
                          value={adjustment.kind}
                          onChange={(event) =>
                            setAdjustments((current) =>
                              current.map((a, i) => (i === index ? { ...a, kind: event.target.value as 'addition' | 'deduction' } : a)),
                            )
                          }
                          className="h-9 w-32"
                        >
                          <option value="addition">Add</option>
                          <option value="deduction">Deduct</option>
                        </Select>
                        <button onClick={() => setAdjustments((current) => current.filter((_, i) => i !== index))} aria-label="Remove">
                          <Trash2 className="size-4 text-[var(--color-ink-faint)] hover:text-[var(--color-danger)]" />
                        </button>
                      </div>
                    </td>
                    <td className="py-2 pl-4">
                      <MoneyInput
                        value={adjustment.amount}
                        onChange={(amount) => setAdjustments((current) => current.map((a, i) => (i === index ? { ...a, amount } : a)))}
                        className="h-9"
                      />
                    </td>
                  </tr>
                ))}

                {(additions > 0 || deductions > 0) && (
                  <tr className="text-[var(--color-ink-soft)]">
                    <td className="py-2" colSpan={2}>Gross pay</td>
                    <td className="tabular py-2 pl-4 text-right">{formatMoney(subtotal)}</td>
                  </tr>
                )}

                <tr className="text-base font-semibold">
                  <td className="py-3" colSpan={2}>{deductions > 0 ? 'Net pay' : 'Grand total'}</td>
                  <td className="tabular py-3 pl-4 text-right font-display text-xl text-crust-700">
                    {formatMoney(grandTotal)}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                icon={<Plus className="size-4" />}
                onClick={() => setAdjustments((current) => [...current, { label: 'Additional sales', amount: 0, kind: 'addition' }])}
              >
                Add a line
              </Button>

              {/*
                * The three that come off almost every Philippine payslip.
                *
                * Buttons rather than automatic amounts: the contribution
                * tables are set by each agency and change, so a figure this
                * app worked out for itself would be confidently wrong the
                * month after a circular. The label and the placing are the
                * repetitive part; the amount is a number the shop already has.
                *
                * The last amount used for that person is offered, because
                * these barely move between periods.
                */}
              {STATUTORY_DEDUCTIONS.map((label) => (
                <Button
                  key={label}
                  size="sm"
                  disabled={adjustments.some((a) => a.label === label)}
                  onClick={() =>
                    setAdjustments((current) => [
                      ...current,
                      { label, amount: lastAmountFor(label), kind: 'deduction' },
                    ])
                  }
                >
                  − {label}
                </Button>
              ))}

              <div className="flex-1" />
              <Button
                icon={<Printer className="size-4" />}
                disabled={!draft.data}
                onClick={() =>
                  draft.data &&
                  printPayslip({
                    business: draft.data.business,
                    employeeName: draft.data.employee.displayName,
                    position: roleName,
                    issuedOn: new Date().toISOString(),
                    periodStart,
                    periodEnd,
                    lines: draft.data.draft.lines,
                    adjustments,
                    subtotal,
                    grandTotal,
                  })
                }
              >
                Print
              </Button>
              <Button variant="primary" loading={issue.isPending} onClick={() => issue.mutate()}>
                Issue payslip
              </Button>
            </div>
          </>
        )}
      </Card>

      <PayslipHistory
        onEdit={(slip) => {
          /*
           * The builder only lists people still on the team, and picking an id
           * it does not hold falls back to whoever is first — so editing a
           * leaver's payslip would quietly rebuild it for somebody else and
           * delete the original. Refuse, and say which of the two it is.
           */
          if (!employees.some((e) => e.id === slip.employeeId)) {
            toast.push({
              tone: 'warn',
              title: `${slip.employee.displayName} is no longer on the team`,
              body: 'The builder only covers current staff, so this slip cannot be rebuilt here. Void it if it is wrong.',
            });
            return;
          }
          setEmployeeId(slip.employeeId);
          setPeriodStart(slip.periodStart);
          setPeriodEnd(slip.periodEnd);
          setAdjustments(slip.adjustments.map((a) => ({ label: a.label, amount: a.amount, kind: a.kind })));
          setReplacing({ id: slip.id, payslipNo: slip.payslipNo });
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
    </div>
  );
}

/**
 * Every payslip that has been issued, most recent pay period first.
 *
 * Until this existed a payslip became unreachable the moment it was printed.
 * The figures were stored, `GET /payslips` was live, and the employee record
 * even carried the last twelve — the app fetched them and dropped them on the
 * floor. So reprinting last fortnight's slip, or answering "what did we deduct
 * in August", meant opening the database. The shop needs that paper twice:
 * once for the person, once for BIR.
 *
 * Nothing here has to be told when a slip is issued: issuing invalidates the
 * `payslips` query above, and React Query matches that by prefix, so a slip
 * appears in this list the instant it is written.
 */
function PayslipHistory({ onEdit }: { onEdit: (slip: PayslipWithDetail) => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [opening, setOpening] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);

  /*
   * Asked for explicitly, because the endpoint defaults to 50 — about a year
   * of fortnightly pay, after which a history quietly stops being one. A
   * thousand is roughly seventy years for this shop, and the request costs the
   * same either way.
   */
  const payslips = useQuery({
    queryKey: ['payslips', 'all'],
    queryFn: () => api.staff.payslips(undefined, 1000),
  });
  const roles = useQuery({ queryKey: ['roles'], queryFn: api.staff.roles });
  /*
   * Names come from the full list, leavers included. A payslip outlives the
   * job it paid for, and a history showing a blank where a former baker's name
   * belongs is worse than no history at all.
   */
  const everyone = useQuery({
    queryKey: ['employees', 'all'],
    queryFn: () => api.staff.employees(true),
  });

  const slips = payslips.data ?? [];
  const nameOf = (id: string) => everyone.data?.find((person) => person.id === id)?.displayName ?? '—';

  const periodLabel = (start: string, end: string) => {
    const on = (date: string, withYear: boolean) =>
      new Date(`${date}T00:00:00Z`).toLocaleDateString('en-PH', {
        day: 'numeric',
        month: 'short',
        ...(withYear ? { year: 'numeric' } : {}),
        timeZone: 'UTC',
      });
    return `${on(start, false)} – ${on(end, true)}`;
  };

  const reprint = async (id: string) => {
    setOpening(id);
    try {
      const slip = await api.staff.payslip(id);
      printPayslip({
        payslipNo: slip.payslipNo,
        business: slip.business,
        employeeName: slip.employee.displayName,
        position: roles.data?.find((role) => role.id === slip.employee.roleId)?.name ?? null,
        /*
         * The day it was issued, not today. A reprint has to be the same
         * document as the original — a slip that redates itself each time it
         * is printed is not a record of anything.
         */
        issuedOn: slip.issuedOn,
        periodStart: slip.periodStart,
        periodEnd: slip.periodEnd,
        /*
         * A stored line carries `kind: null` where a draft carries nothing at
         * all, and the two mean the same thing: no block, so print the grid.
         * The printer decides its layout on `some(line => line.kind)`, which
         * reads both as false — but the types differ, so the null is mapped
         * away here rather than loosened there.
         */
        lines: slip.lines.map((line) => ({ ...line, kind: line.kind ?? undefined })),
        adjustments: slip.adjustments,
        subtotal: slip.subtotal,
        grandTotal: slip.grandTotal,
      });
    } catch {
      toast.push({
        tone: 'warn',
        title: 'Could not open that payslip',
        body: 'It is still on file — try again in a moment.',
      });
    } finally {
      setOpening(null);
    }
  };

  const detail = useQuery({
    queryKey: ['payslip', viewing],
    queryFn: () => api.staff.payslip(viewing!),
    enabled: Boolean(viewing),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['payslips'] });
    queryClient.invalidateQueries({ queryKey: ['payslip'] });
  };

  const voidSlip = useMutation({
    mutationFn: (id: string) => api.staff.voidPayslip(id),
    onSuccess: (slip) => {
      refresh();
      toast.push({ tone: 'ok', title: `${slip.payslipNo} voided`, body: 'It stays on file, marked cancelled.' });
      setViewing(null);
    },
    onError: (error: Error) => toast.push({ tone: 'warn', title: 'Could not void it', body: error.message }),
  });

  /*
   * Marking a slip paid.
   *
   * The server and the client method have both been here all along; there was
   * simply no button, so a shop could issue wages and never record handing
   * them over. Which way it was paid is asked rather than assumed, because it
   * is the one detail nobody can reconstruct afterwards.
   */
  const [paying, setPaying] = useState<Payslip | null>(null);
  const [method, setMethod] = useState('cash');

  const markPaid = useMutation({
    mutationFn: ({ id, how }: { id: string; how: string }) => api.staff.markPaid(id, how),
    onSuccess: (slip) => {
      refresh();
      setPaying(null);
      toast.push({ tone: 'ok', title: slip.payslipNo + ' marked paid' });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not mark it paid', body: error.message }),
  });

  const destroy = useMutation({
    mutationFn: (id: string) => api.staff.deletePayslip(id),
    onSuccess: () => {
      refresh();
      toast.push({ tone: 'ok', title: 'Payslip deleted' });
      setViewing(null);
    },
    /*
     * The server refuses to delete a paid slip and says why. That sentence is
     * the useful part — it names voiding as the way out — so it is shown
     * rather than replaced with something vaguer.
     */
    onError: (error: Error) => toast.push({ tone: 'warn', title: 'Kept on file', body: error.message }),
  });

  return (
    <Card className="p-4">
      <SectionHeading title="History" hint={`${slips.length} issued`} />

      {payslips.isLoading ? (
        <Skeleton className="h-24" />
      ) : slips.length === 0 ? (
        <EmptyState
          icon={<Wallet className="size-5" />}
          title="No payslips issued yet"
          description="Once you issue one above it stays here, and can be printed again at any time."
        />
      ) : (
        <table className="w-full text-sm">
          <tbody className="divide-y divide-[var(--color-line)]">
            {slips.map((slip) => (
              <tr key={slip.id} className={cx(slip.status === 'void' && 'opacity-50')}>
                <td className="py-2">
                  <button
                    type="button"
                    onClick={() => setViewing(slip.id)}
                    className="text-left hover:underline"
                  >
                    <span className="font-medium">{nameOf(slip.employeeId)}</span>
                    <span className="block text-xs text-[var(--color-ink-soft)]">
                      {slip.payslipNo} · {periodLabel(slip.periodStart, slip.periodEnd)}
                    </span>
                  </button>
                </td>
                <td className="py-2 text-right">
                  {slip.status === 'paid' ? (
                    <Badge tone="ok">Paid</Badge>
                  ) : slip.status === 'void' ? (
                    <Badge tone="danger">Void</Badge>
                  ) : (
                    <Badge tone="neutral">Unpaid</Badge>
                  )}
                </td>
                <td className="tabular py-2 text-right font-medium">{formatMoney(slip.grandTotal)}</td>
                <td className="py-2 pl-2 text-right">
                  <div className="flex justify-end gap-1.5">
                    {/*
                      * On the row as well as inside the slip. Wages are paid a
                      * handful at a time on the same afternoon, and opening
                      * each one to tick it off is five dialogs for one errand.
                      */}
                    {slip.status === 'issued' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={<Banknote className="size-4" />}
                        onClick={() => {
                          setMethod('cash');
                          setPaying(slip);
                        }}
                      >
                        Mark paid
                      </Button>
                    )}
                    <Button
                      size="sm"
                      icon={<Printer className="size-4" />}
                      loading={opening === slip.id}
                      onClick={() => reprint(slip.id)}
                    >
                      Print
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {paying && (
        <Modal
          open
          onClose={() => setPaying(null)}
          title={`Mark ${paying.payslipNo} paid`}
          description={`${nameOf(paying.employeeId)} · ${formatMoney(paying.grandTotal)}`}
          footer={
            <>
              <Button variant="ghost" onClick={() => setPaying(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={markPaid.isPending}
                onClick={() => markPaid.mutate({ id: paying.id, how: method })}
              >
                Mark paid
              </Button>
            </>
          }
        >
          {/*
            * Asked rather than assumed. Which way wages went out is the one
            * detail nobody can work out afterwards from the figures alone, and
            * it is the question anybody reconciling cash will want answered.
            */}
          <Field label="How was it paid">
            <Select value={method} onChange={(event) => setMethod(event.target.value)}>
              <option value="cash">Cash</option>
              <option value="gcash">GCash</option>
              <option value="bank">Bank transfer</option>
            </Select>
          </Field>
        </Modal>
      )}

      {viewing && (
        <Modal
          open
          onClose={() => setViewing(null)}
          size="lg"
          title={detail.data ? detail.data.payslipNo : 'Payslip'}
          description={
            detail.data
              ? `${detail.data.employee.displayName} · ${periodLabel(detail.data.periodStart, detail.data.periodEnd)}`
              : undefined
          }
          footer={
            <>
              <Button
                variant="ghost"
                loading={destroy.isPending}
                onClick={() => {
                  if (!detail.data) return;
                  /*
                   * Named, not "are you sure". The number is the thing that
                   * will be missing from the run afterwards, so it is what the
                   * question asks about.
                   */
                  if (window.confirm(`Delete ${detail.data.payslipNo} permanently? Voiding keeps it on file instead.`)) {
                    destroy.mutate(detail.data.id);
                  }
                }}
              >
                Delete
              </Button>
              <Button
                variant="ghost"
                loading={voidSlip.isPending}
                disabled={detail.data?.status === 'void'}
                onClick={() => detail.data && voidSlip.mutate(detail.data.id)}
              >
                {detail.data?.status === 'void' ? 'Voided' : 'Void'}
              </Button>
              {detail.data?.status === 'issued' && (
                <Button
                  variant="primary"
                  icon={<Banknote className="size-4" />}
                  onClick={() => {
                    setMethod('cash');
                    setPaying(detail.data!);
                  }}
                >
                  Mark paid
                </Button>
              )}
              <Button
                variant="secondary"
                disabled={!detail.data}
                onClick={() => {
                  if (!detail.data) return;
                  onEdit(detail.data);
                  setViewing(null);
                }}
              >
                Edit
              </Button>
              <Button
                variant="secondary"
                icon={<Printer className="size-4" />}
                loading={opening === viewing}
                onClick={() => reprint(viewing)}
              >
                Print
              </Button>
              <Button variant="secondary" onClick={() => setViewing(null)}>
                Close
              </Button>
            </>
          }
        >
          {!detail.data ? (
            <Skeleton className="h-48" />
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {detail.data.status === 'paid' ? (
                  <Badge tone="ok">Paid</Badge>
                ) : detail.data.status === 'void' ? (
                  <Badge tone="danger">Void</Badge>
                ) : (
                  <Badge tone="neutral">Unpaid</Badge>
                )}
                <span className="text-[var(--color-ink-soft)]">
                  Issued {new Date(`${detail.data.issuedOn}T00:00:00Z`).toLocaleDateString('en-PH', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    timeZone: 'UTC',
                  })}
                </span>
              </div>

              <table className="w-full text-sm">
                <tbody className="divide-y divide-[var(--color-line)]">
                  {detail.data.lines.map((line) => (
                    <tr key={line.id}>
                      <td className="py-2">
                        {line.label}
                        {line.note && (
                          <span className="block text-xs text-[var(--color-ink-faint)]">{line.note}</span>
                        )}
                      </td>
                      <td className="tabular py-2 text-right text-[var(--color-ink-soft)]">
                        {line.qty} {line.unitLabel} × {formatMoney(line.rate)}
                      </td>
                      <td className="tabular py-2 text-right font-medium">{formatMoney(line.amount)}</td>
                    </tr>
                  ))}
                  {detail.data.adjustments.map((adjustment) => (
                    <tr key={adjustment.id}>
                      <td className="py-2" colSpan={2}>
                        {adjustment.kind === 'deduction' ? '− ' : '+ '}
                        {adjustment.label}
                      </td>
                      <td className="tabular py-2 text-right font-medium">
                        {adjustment.kind === 'deduction' ? '−' : ''}
                        {formatMoney(adjustment.amount)}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="py-2 font-semibold" colSpan={2}>
                      Net pay
                    </td>
                    <td className="tabular py-2 text-right text-base font-semibold">
                      {formatMoney(detail.data.grandTotal)}
                    </td>
                  </tr>
                </tbody>
              </table>

              <p className="text-xs text-[var(--color-ink-faint)]">
                Edit rebuilds this slip from the work log and the current rates, then replaces it. The figures are
                worked out, not stored by hand, so there is nothing here that can be typed over.
              </p>
            </div>
          )}
        </Modal>
      )}
    </Card>
  );
}

function EmployeeModal({ employee, onClose }: { employee?: EmployeeWithPay; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const roles = useQuery({ queryKey: ['roles'], queryFn: api.staff.roles });

  const [positionsOpen, setPositionsOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const [form, setForm] = useState({
    firstName: employee?.firstName ?? '',
    middleName: employee?.middleName ?? '',
    lastName: employee?.lastName ?? '',
    roleId: employee?.roleId ?? 'role-cashier',
    phone: employee?.phone ?? '',
    address: employee?.address ?? '',
    emergencyContactName: employee?.emergencyContactName ?? '',
    emergencyContact: employee?.emergencyContact ?? '',
    defaultShiftStart: employee?.defaultShiftStart ?? '',
    defaultShiftEnd: employee?.defaultShiftEnd ?? '',
    payType: employee?.payType ?? ('monthly' as Employee['payType']),
    monthlySalary: employee?.monthlySalary ?? 0,
    shiftRate: employee?.shiftRate ?? 0,
    dailyRate: employee?.dailyRate ?? 0,
    hourlyRate: employee?.hourlyRate ?? 0,
  });

  // `id` is absent on a rate that has just been typed in and does not exist yet.
  const [payItems, setPayItems] = useState<
    { id?: string; label: string; unitLabel: string; rate: number }[]
  >(
    employee?.payItems
      .filter((item) => item.isActive)
      .map((item) => ({ id: item.id, label: item.label, unitLabel: item.unitLabel, rate: item.rate })) ?? [],
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['employees'] });

  const save = useMutation({
    mutationFn: async () => {
      /*
       * The rates go with the rest of the form as one list, so a rate taken
       * off the form is actually removed. Saving them one at a time afterwards
       * could only ever add and update — which is why somebody moved onto a
       * daily wage kept their piece rates for months.
       *
       * Sent only for piece-rate staff: on any other pay type they are not
       * shown, and an empty list would silently retire rates that should keep
       * paying if the shop moves them back.
       */
      const body = {
        ...form,
        /*
         * An untouched time field is "not recorded", not "the empty time".
         * Storing the empty string made the rota open on a blank time for
         * anyone whose card had been saved without their hours filled in —
         * including everybody whose card was edited for some other reason.
         */
        defaultShiftStart: form.defaultShiftStart || null,
        defaultShiftEnd: form.defaultShiftEnd || null,
        ...(form.payType === 'piece_rate' ? { payItems } : {}),
      };
      return employee ? api.staff.update(employee.id, body) : api.staff.create(body);
    },
    onSuccess: (saved) => {
      refresh();
      toast.push({ tone: 'ok', title: `${saved.displayName} saved` });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const reinstate = useMutation({
    mutationFn: () => api.staff.reinstate(employee!.id),
    onSuccess: (saved) => {
      refresh();
      toast.push({ tone: 'ok', title: `${saved.displayName} is back on the team` });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not do that', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={employee ? employee.displayName : 'New employee'}
      description={
        employee && !employee.isActive
          ? `Left ${employee.endedOn ?? 'the shop'}${employee.leavingReason ? ` — ${employee.leavingReason}` : ''}. Their record is kept.`
          : undefined
      }
      footer={
        <>
          {employee &&
            (employee.isActive ? (
              <Button variant="ghost" className="text-[var(--color-danger)]" onClick={() => setLeaving(true)}>
                They have left
              </Button>
            ) : (
              <Button variant="ghost" loading={reinstate.isPending} onClick={() => reinstate.mutate()}>
                Back on the team
              </Button>
            ))}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!form.firstName || !form.lastName} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="First name" required>
            <Input value={form.firstName} onChange={(event) => setForm({ ...form, firstName: event.target.value })} />
          </Field>
          <Field label="Middle name">
            <Input value={form.middleName} onChange={(event) => setForm({ ...form, middleName: event.target.value })} />
          </Field>
          <Field label="Last name" required>
            <Input value={form.lastName} onChange={(event) => setForm({ ...form, lastName: event.target.value })} />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Position">
            <div className="flex gap-2">
              <Select
                value={form.roleId}
                onChange={(event) => setForm({ ...form, roleId: event.target.value })}
                className="min-w-0 flex-1"
              >
                {roles.data?.map((role) => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </Select>
              {/*
                * Hiring somebody whose job is not on the list is exactly when
                * you need to add it, so the way to do that is here rather than
                * somewhere you have to go and find first.
                */}
              <Button
                className="shrink-0 px-2.5"
                aria-label="Add or rename positions"
                title="Add or rename positions"
                onClick={() => setPositionsOpen(true)}
                icon={<Plus className="size-4" />}
              />
            </div>
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} inputMode="tel" />
          </Field>
        </div>

        <Field label="Address">
          <Textarea
            rows={2}
            value={form.address}
            onChange={(event) => setForm({ ...form, address: event.target.value })}
            placeholder="House or unit, street, barangay, city"
          />
        </Field>

        {/*
          * Two fields rather than one line of free text. This is read in a
          * hurry by somebody who needs to know who they are about to ring.
          */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="In an emergency, call" hint="Their name and how they are related.">
            <Input
              value={form.emergencyContactName}
              onChange={(event) => setForm({ ...form, emergencyContactName: event.target.value })}
              placeholder="Maria Asis (mother)"
            />
          </Field>
          <Field label="Emergency contact number">
            <Input
              value={form.emergencyContact}
              onChange={(event) => setForm({ ...form, emergencyContact: event.target.value })}
              inputMode="tel"
              placeholder="0917 000 0000"
            />
          </Field>
        </div>

        {/*
          * What the rota opens on for this person. Not a contract — every
          * shift can still be changed — but scheduling a week used to mean
          * correcting the same two times seven days running.
          */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Usually starts" hint="Used to fill in a new shift on the rota.">
            <Input
              type="time"
              value={form.defaultShiftStart}
              onChange={(event) => setForm({ ...form, defaultShiftStart: event.target.value })}
            />
          </Field>
          <Field label="Usually ends">
            <Input
              type="time"
              value={form.defaultShiftEnd}
              onChange={(event) => setForm({ ...form, defaultShiftEnd: event.target.value })}
            />
          </Field>
        </div>

        <Field label="How they are paid">
          <Select value={form.payType} onChange={(event) => setForm({ ...form, payType: event.target.value as Employee['payType'] })}>
            <option value="monthly">Monthly salary</option>
            <option value="daily">Daily rate</option>
            <option value="hourly">Hourly rate</option>
            <option value="per_shift">Per shift worked</option>
            <option value="piece_rate">Per piece produced or delivered</option>
          </Select>
        </Field>

        {form.payType === 'monthly' && (
          <Field label="Monthly salary">
            <MoneyInput value={form.monthlySalary} onChange={(value) => setForm({ ...form, monthlySalary: value })} />
          </Field>
        )}
        {form.payType === 'per_shift' && (
          <Field label="Rate per shift">
            <MoneyInput value={form.shiftRate} onChange={(value) => setForm({ ...form, shiftRate: value })} />
          </Field>
        )}
        {form.payType === 'daily' && (
          <Field label="Daily rate">
            <MoneyInput value={form.dailyRate} onChange={(value) => setForm({ ...form, dailyRate: value })} />
          </Field>
        )}
        {form.payType === 'hourly' && (
          <Field label="Hourly rate">
            <MoneyInput value={form.hourlyRate} onChange={(value) => setForm({ ...form, hourlyRate: value })} />
          </Field>
        )}

        {form.payType === 'piece_rate' && (
          <div>
            <p className="mb-2 text-sm font-medium">Piece rates</p>
            <div className="space-y-2">
              {payItems.map((item, index) => (
                <div key={index} className="grid grid-cols-12 items-end gap-2">
                  <div className="col-span-5">
                    <Input
                      placeholder="Baked loaves"
                      value={item.label}
                      onChange={(event) => setPayItems((c) => c.map((p, i) => (i === index ? { ...p, label: event.target.value } : p)))}
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      placeholder="loaves"
                      value={item.unitLabel}
                      onChange={(event) => setPayItems((c) => c.map((p, i) => (i === index ? { ...p, unitLabel: event.target.value } : p)))}
                    />
                  </div>
                  <div className="col-span-3">
                    <MoneyInput value={item.rate} onChange={(rate) => setPayItems((c) => c.map((p, i) => (i === index ? { ...p, rate } : p)))} />
                  </div>
                  <button className="col-span-1 pb-3" onClick={() => setPayItems((c) => c.filter((_, i) => i !== index))} aria-label="Remove rate">
                    <Trash2 className="size-4 text-[var(--color-ink-faint)] hover:text-[var(--color-danger)]" />
                  </button>
                </div>
              ))}
              <Button
                size="sm"
                icon={<Plus className="size-4" />}
                onClick={() =>
                  setPayItems((current) => [...current, { id: undefined, label: '', unitLabel: 'pcs', rate: 0 }])
                }
              >
                Add a rate
              </Button>
            </div>
          </div>
        )}
      </div>

      {positionsOpen && (
        <PositionsModal onClose={() => setPositionsOpen(false)} />
      )}

      {leaving && employee && (
        <OffboardModal
          employee={employee}
          onCancel={() => setLeaving(false)}
          onDone={() => {
            setLeaving(false);
            refresh();
            onClose();
          }}
        />
      )}
    </Modal>
  );
}

/**
 * Recording that somebody has left.
 *
 * Deliberately not a delete. Their payslips name them, the days counted
 * against their rates are the workings behind those payslips, and a year later
 * "who was on that week" is a question somebody will ask. They come off the
 * team list, the rota and the payslip picker; everything else stays.
 */
function OffboardModal({
  employee,
  onCancel,
  onDone,
}: {
  employee: EmployeeWithPay;
  onCancel: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [endedOn, setEndedOn] = useState(shopDate());
  const [reason, setReason] = useState('');

  const offboard = useMutation({
    mutationFn: () => api.staff.offboard(employee.id, { endedOn, reason }),
    onSuccess: (saved) => {
      toast.push({
        tone: 'ok',
        title: `${saved.displayName} marked as having left`,
        body: 'Their record and payslips are kept, under Former on the Team tab.',
      });
      onDone();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not do that', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onCancel}
      title={`${employee.displayName} has left`}
      description="Their record stays on file. You can put them back on the team at any time."
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" loading={offboard.isPending} onClick={() => offboard.mutate()}>
            Mark as left
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Last day">
          <Input type="date" value={endedOn} onChange={(event) => setEndedOn(event.target.value)} />
        </Field>
        <Field label="Reason" hint="Optional, and only you will read it.">
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Resigned, end of contract, dismissed…"
          />
        </Field>
      </div>
    </Modal>
  );
}
